import { useState } from "react";
import { Ban, CheckCircle2, ShieldCheck, XCircle } from "lucide-react";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Label } from "@/core/components/ui/label";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useApproveWalletKyc } from "@/features/wallet/api/use-approve-wallet-kyc";
import { useSetWalletStatus } from "@/features/wallet/api/use-set-wallet-status";
import type { WalletDto } from "@/features/wallet/api/dto";
import { CloseWalletConfirmDialog } from "@/features/wallet/components/wallets/close-wallet-confirm-dialog";
import {
  WALLET_KYC_LEVELS,
  type WalletKycLevel,
  type WalletStatus,
} from "@/features/shared/api/enums";

/**
 * Statuts cibles proposés selon le statut courant (guide §6.3 : cibles
 * admises ACTIVE, SUSPENDED, CLOSED). Un portefeuille PENDING_KYC ne passe
 * en ACTIVE que via `kyc-approval` (étape 6), jamais via /status.
 */
type ActionableWalletStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";

const nextStatuses = (status?: WalletStatus): ActionableWalletStatus[] => {
  switch (status) {
    case "ACTIVE":
      return ["SUSPENDED", "CLOSED"];
    case "SUSPENDED":
      return ["ACTIVE", "CLOSED"];
    case "PENDING_KYC":
    case "CLOSED":
    default:
      return [];
  }
};

const STATUS_ACTION_LABEL: Record<ActionableWalletStatus, string> = {
  ACTIVE: "wallet.wallets.actions.activate",
  SUSPENDED: "wallet.wallets.actions.suspend",
  CLOSED: "wallet.wallets.actions.close",
};

const STATUS_ACTION_ICON: Record<ActionableWalletStatus, typeof Ban> = {
  ACTIVE: CheckCircle2,
  SUSPENDED: Ban,
  CLOSED: XCircle,
};

/**
 * Boutons d'orchestration du détail portefeuille : changement de statut
 * (`POST /Wallets/{id}/status`) et approbation KYC
 * (`POST /Wallets/{id}/kyc-approval?kycLevel=...`, étape 6 — Kafka non
 * câblé, appel HTTP explicite).
 *
 * Pas de garde par rôle ici : le helper d'autorisation arrive en phase 1.2
 * — l'API tranche (kyc-approval : flashpay-service/superadmin/admin ;
 * status : flashpay-superadmin/admin).
 */
export function WalletStatusActions({ wallet }: { wallet: WalletDto }) {
  const { t } = useTypedTranslation();
  // Les clés wallet.wallets.* vivent dans features/wallet/i18n/wallets.json
  // en attendant leur fusion dans src/locales par le coordinateur.
  const tWallet = (key: string) => t(key as TFunctionType);

  const walletId = wallet.id ?? "";
  const setWalletStatus = useSetWalletStatus();
  const approveKyc = useApproveWalletKyc(walletId);
  const [kycLevel, setKycLevel] = useState<WalletKycLevel>(
    wallet.kycLevel ?? "TIER1"
  );
  const [closeOpen, setCloseOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<
    Exclude<ActionableWalletStatus, "CLOSED"> | null
  >(null);
  const [kycOpen, setKycOpen] = useState(false);

  const targets = nextStatuses(wallet.status);
  const pending = setWalletStatus.isPending || approveKyc.isPending;

  if (!walletId || (targets.length === 0 && wallet.status !== "PENDING_KYC")) {
    return null;
  }

  const onSetStatus = (status: WalletStatus) =>
    setWalletStatus.mutate(
      { walletId, status },
      {
        onSuccess: () => {
          notifSilence(tWallet("wallet.wallets.feedback.statusUpdated"));
          if (status === "CLOSED") setCloseOpen(false);
          else setStatusTarget(null);
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {tWallet("wallet.wallets.status.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {targets.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {targets.map((status) => {
              const Icon = STATUS_ACTION_ICON[status];
              const destructive = status === "SUSPENDED" || status === "CLOSED";
              return (
                <Button
                  key={status}
                  variant={destructive ? "destructive" : "outline"}
                  disabled={pending}
                  onClick={() =>
                    status === "CLOSED"
                      ? setCloseOpen(true)
                      : setStatusTarget(status)
                  }
                >
                  {setWalletStatus.isPending &&
                  setWalletStatus.variables?.status === status ? (
                    <ButtonSpinner />
                  ) : (
                    <Icon className="mr-2 h-4 w-4" />
                  )}
                  {tWallet(STATUS_ACTION_LABEL[status])}
                </Button>
              );
            })}
          </div>
        ) : null}

        {wallet.status === "PENDING_KYC" ? (
          <div className="flex flex-wrap items-end gap-3 rounded-md border border-dashed border-border p-4">
            <div className="space-y-1.5">
              <Label>{tWallet("wallet.wallets.kycApproval.level")}</Label>
              <Select
                value={kycLevel}
                onValueChange={(v) => setKycLevel(v as WalletKycLevel)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WALLET_KYC_LEVELS.map((v) => (
                    <SelectItem key={v} value={v} className="notranslate">
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setKycOpen(true)} disabled={pending}>
              {approveKyc.isPending ? (
                <ButtonSpinner />
              ) : (
                <ShieldCheck className="mr-2 h-4 w-4" />
              )}
              {tWallet("wallet.wallets.kycApproval.submit")}
            </Button>
            <p className="w-full text-sm text-muted-foreground">
              {tWallet("wallet.wallets.kycApproval.title")}
            </p>
          </div>
        ) : null}
      </CardContent>
      <CloseWalletConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        walletId={walletId}
        walletNumber={wallet.walletNumber}
        pending={
          setWalletStatus.isPending &&
          setWalletStatus.variables?.status === "CLOSED"
        }
        onConfirm={() => onSetStatus("CLOSED")}
      />
      <MutationConfirmPreview
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (!open) setStatusTarget(null);
        }}
        data={{
          id: walletId,
          walletNumber: wallet.walletNumber,
          currentStatus: wallet.status,
          nextStatus: statusTarget,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          statusTarget ? tWallet(STATUS_ACTION_LABEL[statusTarget]) : undefined
        }
        t={tWallet}
        pending={setWalletStatus.isPending}
        onConfirm={() => {
          if (statusTarget) onSetStatus(statusTarget);
        }}
      />
      <MutationConfirmPreview
        open={kycOpen}
        onOpenChange={setKycOpen}
        data={{
          id: walletId,
          walletNumber: wallet.walletNumber,
          kycLevel,
          status: wallet.status,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={tWallet("wallet.wallets.kycApproval.submit")}
        t={tWallet}
        pending={approveKyc.isPending}
        onConfirm={() => {
          approveKyc.mutate(
            { kycLevel },
            {
              onSuccess: () => {
                notifSilence(tWallet("wallet.wallets.feedback.kycApproved"));
                setKycOpen(false);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </Card>
  );
}
